import { useState, useEffect } from 'react';
import {
    Plus,
    Edit01,
    Trash01,
    SearchLg
} from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Tabs } from '@/components/application/tabs/tabs';
import { Modal, DialogTrigger, Dialog, ModalOverlay } from '@/components/application/modals/modal';
import { LanguageSelector } from '@/components/application/language-selector/language-selector';
import { ThemeSwitcher } from '@/components/application/theme-switcher/theme-switcher';
import { apiKeyService, serviceConfigService } from '@/data/mock-settings';
import type { ApiKey, ServiceConfig, ServiceCategory } from '@/types/settings';
import { useTranslation } from 'react-i18next';

interface ApiKeyFormData {
    name: string;
    service: string;
    category: ServiceCategory;
    type: string;
    value: string;
    description: string;
    isActive: boolean;
}

interface ServiceConfigFormData {
    name: string;
    category: ServiceCategory;
    baseUrl: string;
    isEnabled: boolean;
    config: string;
}

export const SettingsPage = () => {
    const { t } = useTranslation(['pages:settings-admin', 'common']);
    const [activeTab, setActiveTab] = useState('api-keys');
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
    const [serviceConfigs, setServiceConfigs] = useState<ServiceConfig[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<ServiceCategory | ''>('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalType, setModalType] = useState<'api-key' | 'service-config'>('api-key');
    const [editingItem, setEditingItem] = useState<ApiKey | ServiceConfig | null>(null);

    const [apiKeyForm, setApiKeyForm] = useState<ApiKeyFormData>({
        name: '',
        service: '',
        category: 'AI/ML',
        type: 'API Key',
        value: '',
        description: '',
        isActive: true
    });

    const [serviceConfigForm, setServiceConfigForm] = useState<ServiceConfigFormData>({
        name: '',
        category: 'AI/ML',
        baseUrl: '',
        isEnabled: true,
        config: ''
    });

    const categoryOptions = [
        { id: '', label: t('apiKeys.allCategories') },
        { id: 'AI/ML', label: 'AI/ML' },
        { id: 'Payment', label: 'Payment' },
        { id: 'Email', label: 'Email' },
        { id: 'Cloud', label: 'Cloud' },
        { id: 'Analytics', label: 'Analytics' },
        { id: 'Other', label: 'Other' }
    ];

    const typeOptions = [
        { id: 'API Key', label: 'API Key' },
        { id: 'Secret Key', label: 'Secret Key' },
        { id: 'Token', label: 'Token' },
        { id: 'Password', label: 'Password' },
        { id: 'Certificate', label: 'Certificate' }
    ];

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);

            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 500));

            const apiKeysData = apiKeyService.getAll();
            const serviceConfigsData = serviceConfigService.getAll();

            setApiKeys(apiKeysData);
            setServiceConfigs(serviceConfigsData);
            setIsLoading(false);
        };

        loadData();
    }, []);

    const handleOpenModal = (type: 'api-key' | 'service-config', item?: ApiKey | ServiceConfig) => {
        setModalType(type);
        setEditingItem(item || null);

        if (item) {
            if (type === 'api-key') {
                const apiKey = item as ApiKey;
                setApiKeyForm({
                    name: apiKey.name,
                    service: apiKey.service,
                    category: apiKey.category,
                    type: apiKey.type,
                    value: apiKey.value,
                    description: apiKey.description || '',
                    isActive: apiKey.isActive
                });
            } else {
                const serviceConfig = item as ServiceConfig;
                setServiceConfigForm({
                    name: serviceConfig.name,
                    category: serviceConfig.category,
                    baseUrl: serviceConfig.baseUrl || '',
                    isEnabled: serviceConfig.isEnabled,
                    config: JSON.stringify(serviceConfig.config, null, 2)
                });
            }
        } else {
            // Reset forms for new items
            setApiKeyForm({
                name: '',
                service: '',
                category: 'AI/ML',
                type: 'API Key',
                value: '',
                description: '',
                isActive: true
            });
            setServiceConfigForm({
                name: '',
                category: 'AI/ML',
                baseUrl: '',
                isEnabled: true,
                config: ''
            });
        }

        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
    };

    const handleSaveApiKey = () => {
        if (editingItem) {
            apiKeyService.update(editingItem.id, { ...apiKeyForm, id: editingItem.id, type: apiKeyForm.type as any });
        } else {
            apiKeyService.create({ ...apiKeyForm, type: apiKeyForm.type as any });
        }

        // Reload data
        setApiKeys(apiKeyService.getAll());
        handleCloseModal();
    };

    const handleSaveServiceConfig = () => {
        if (editingItem) {
            serviceConfigService.update(editingItem.id, {
                ...serviceConfigForm,
                id: editingItem.id,
                config: JSON.parse(serviceConfigForm.config || '{}')
            });
        } else {
            serviceConfigService.create({
                ...serviceConfigForm,
                config: JSON.parse(serviceConfigForm.config || '{}')
            });
        }

        // Reload data
        setServiceConfigs(serviceConfigService.getAll());
        handleCloseModal();
    };

    const handleDelete = (id: string, type: 'api-key' | 'service-config') => {
        if (confirm(t('modal.confirmDelete'))) {
            if (type === 'api-key') {
                apiKeyService.delete(id);
                setApiKeys(apiKeyService.getAll());
            } else {
                serviceConfigService.delete(id);
                setServiceConfigs(serviceConfigService.getAll());
            }
        }
    };

    const getStatusBadge = (isActive: boolean) => {
        return isActive ? (
            <Badge color="success" size="sm">{t('apiKeys.status.active')}</Badge>
        ) : (
            <Badge color="error" size="sm">{t('apiKeys.status.inactive')}</Badge>
        );
    };

    const getCategoryBadge = (category: ServiceCategory) => {
        const colors = {
            'AI/ML': 'brand',
            'Payment': 'success',
            'Email': 'blue',
            'Cloud': 'purple',
            'Analytics': 'orange',
            'Other': 'gray'
        } as const;

        return (
            <Badge color={colors[category] || 'gray'} size="sm">
                {category}
            </Badge>
        );
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t('title')}</h1>
                    <p className="text-tertiary mt-1">
                        {t('subtitle')}
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <Tabs selectedKey={activeTab} onSelectionChange={(key) => setActiveTab(key as string)}>
                <Tabs.List items={[
                    { id: "api-keys", label: t('tabs.apiKeys'), badge: apiKeys.length },
                    { id: "service-configs", label: t('tabs.serviceConfigs'), badge: serviceConfigs.length },
                    { id: "preferences", label: t('tabs.preferences') }
                ]}>
                    <Tabs.Item id="api-keys" label={t('tabs.apiKeys')} badge={apiKeys.length} />
                    <Tabs.Item id="service-configs" label={t('tabs.serviceConfigs')} badge={serviceConfigs.length} />
                    <Tabs.Item id="preferences" label={t('tabs.preferences')} />
                </Tabs.List>

                {/* API Keys Tab */}
                <Tabs.Panel id="api-keys">
                    <div className="space-y-6">
                        {/* Filters */}
                        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                <Input
                                    placeholder={t('apiKeys.searchPlaceholder')}
                                    value={searchTerm}
                                    onChange={setSearchTerm}
                                    icon={SearchLg}
                                    size="md"
                                />

                                <Select
                                    placeholder={t('apiKeys.categoryPlaceholder')}
                                    items={categoryOptions}
                                    selectedKey={categoryFilter}
                                    onSelectionChange={(key) => setCategoryFilter(key as ServiceCategory | '')}
                                    size="md"
                                >
                                    {(item) => (
                                        <Select.Item key={item.id} id={item.id}>
                                            {item.label}
                                        </Select.Item>
                                    )}
                                </Select>

                                <Button
                                    onClick={() => handleOpenModal('api-key')}
                                    size="md"
                                    color="primary"
                                    iconLeading={Plus}
                                    className="w-full sm:w-auto"
                                >
                                    {t('apiKeys.newKey')}
                                </Button>
                            </div>
                        </div>

                        {/* API Keys Table */}
                        <TableCard.Root>
                            <TableCard.Header
                                title={`${t('apiKeys.title')} (${apiKeys.length})`}
                                description={t('apiKeys.description')}
                            />

                            <Table>
                                <Table.Header>
                                    <Table.Head label={t('apiKeys.headers.name')} isRowHeader />
                                    <Table.Head label={t('apiKeys.headers.service')} />
                                    <Table.Head label={t('apiKeys.headers.category')} />
                                    <Table.Head label={t('apiKeys.headers.type')} />
                                    <Table.Head label={t('apiKeys.headers.status')} />
                                    <Table.Head label={t('apiKeys.headers.actions')} />
                                </Table.Header>
                                <Table.Body>
                                    {apiKeys.map((key) => (
                                        <Table.Row key={key.id}>
                                            <Table.Cell>
                                                <div>
                                                    <div className="font-medium text-primary">{key.name}</div>
                                                    <div className="text-sm text-tertiary">{key.description}</div>
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className="text-sm text-tertiary">{key.service}</span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                {getCategoryBadge(key.category)}
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className="text-sm text-tertiary">{key.type}</span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                {getStatusBadge(key.isActive)}
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        onClick={() => handleOpenModal('api-key', key)}
                                                        size="sm"
                                                        color="secondary"
                                                        iconLeading={Edit01}
                                                    >
                                                        {t('apiKeys.actions.edit')}
                                                    </Button>
                                                    <Button
                                                        onClick={() => handleDelete(key.id, 'api-key')}
                                                        size="sm"
                                                        color="secondary-destructive"
                                                        iconLeading={Trash01}
                                                    >
                                                        {t('apiKeys.actions.delete')}
                                                    </Button>
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </TableCard.Root>
                    </div>
                </Tabs.Panel>

                {/* Service Configs Tab */}
                <Tabs.Panel id="service-configs">
                    <div className="space-y-6">
                        {/* Filters */}
                        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                <Input
                                    placeholder={t('serviceConfigs.searchPlaceholder')}
                                    value={searchTerm}
                                    onChange={setSearchTerm}
                                    icon={SearchLg}
                                    size="md"
                                />

                                <Select
                                    placeholder={t('serviceConfigs.categoryPlaceholder')}
                                    items={categoryOptions}
                                    selectedKey={categoryFilter}
                                    onSelectionChange={(key) => setCategoryFilter(key as ServiceCategory | '')}
                                    size="md"
                                >
                                    {(item) => (
                                        <Select.Item key={item.id} id={item.id}>
                                            {item.label}
                                        </Select.Item>
                                    )}
                                </Select>

                                <Button
                                    onClick={() => handleOpenModal('service-config')}
                                    size="md"
                                    color="primary"
                                    iconLeading={Plus}
                                    className="w-full sm:w-auto"
                                >
                                    {t('serviceConfigs.newService')}
                                </Button>
                            </div>
                        </div>

                        {/* Service Configs Table */}
                        <TableCard.Root>
                            <TableCard.Header
                                title={`${t('serviceConfigs.title')} (${serviceConfigs.length})`}
                                description={t('serviceConfigs.description')}
                            />

                            <Table>
                                <Table.Header>
                                    <Table.Head label={t('serviceConfigs.headers.name')} isRowHeader />
                                    <Table.Head label={t('serviceConfigs.headers.category')} />
                                    <Table.Head label={t('serviceConfigs.headers.baseUrl')} />
                                    <Table.Head label={t('serviceConfigs.headers.status')} />
                                    <Table.Head label={t('serviceConfigs.headers.actions')} />
                                </Table.Header>
                                <Table.Body>
                                    {serviceConfigs.map((config) => (
                                        <Table.Row key={config.id}>
                                            <Table.Cell>
                                                <div>
                                                    <div className="font-medium text-primary">{config.name}</div>
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                {getCategoryBadge(config.category)}
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className="text-sm text-tertiary">{config.baseUrl || 'N/A'}</span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                {getStatusBadge(config.isEnabled)}
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        onClick={() => handleOpenModal('service-config', config)}
                                                        size="sm"
                                                        color="secondary"
                                                        iconLeading={Edit01}
                                                    >
                                                        {t('serviceConfigs.actions.edit')}
                                                    </Button>
                                                    <Button
                                                        onClick={() => handleDelete(config.id, 'service-config')}
                                                        size="sm"
                                                        color="secondary-destructive"
                                                        iconLeading={Trash01}
                                                    >
                                                        {t('serviceConfigs.actions.delete')}
                                                    </Button>
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </TableCard.Root>
                    </div>
                </Tabs.Panel>

                {/* Preferences Tab */}
                <Tabs.Panel id="preferences">
                    <div className="space-y-6">
                        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                            <h2 className="text-lg font-semibold text-primary mb-6">{t('tabs.preferences')}</h2>
                            <div className="space-y-6">
                                <LanguageSelector />
                                <ThemeSwitcher />
                            </div>
                        </div>
                    </div>
                </Tabs.Panel>
            </Tabs>

            {/* Modal */}
            <DialogTrigger isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-6">
                                    {editingItem 
                                        ? (modalType === 'api-key' ? t('apiKeys.modal.edit') : t('serviceConfigs.modal.edit'))
                                        : (modalType === 'api-key' ? t('apiKeys.modal.new') : t('serviceConfigs.modal.new'))
                                    }
                                </h2>

                                {modalType === 'api-key' ? (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                            <Input
                                                label={t('apiKeys.modal.fields.name')}
                                                placeholder={t('apiKeys.modal.fields.namePlaceholder')}
                                                value={apiKeyForm.name}
                                                onChange={(value) => setApiKeyForm({ ...apiKeyForm, name: value })}
                                                isRequired
                                                size="md"
                                            />
                                            <Input
                                                label={t('apiKeys.modal.fields.service')}
                                                placeholder={t('apiKeys.modal.fields.servicePlaceholder')}
                                                value={apiKeyForm.service}
                                                onChange={(value) => setApiKeyForm({ ...apiKeyForm, service: value })}
                                                isRequired
                                                size="md"
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                            <Select
                                                label={t('apiKeys.modal.fields.category')}
                                                items={categoryOptions.filter(opt => opt.id !== '')}
                                                selectedKey={apiKeyForm.category}
                                                onSelectionChange={(key) => setApiKeyForm({ ...apiKeyForm, category: key as ServiceCategory })}
                                                isRequired
                                                size="md"
                                            >
                                                {(item) => (
                                                    <Select.Item key={item.id} id={item.id}>
                                                        {item.label}
                                                    </Select.Item>
                                                )}
                                            </Select>
                                            <Select
                                                label={t('apiKeys.modal.fields.type')}
                                                items={typeOptions}
                                                selectedKey={apiKeyForm.type}
                                                onSelectionChange={(key) => setApiKeyForm({ ...apiKeyForm, type: key as string })}
                                                isRequired
                                                size="md"
                                            >
                                                {(item) => (
                                                    <Select.Item key={item.id} id={item.id}>
                                                        {item.label}
                                                    </Select.Item>
                                                )}
                                            </Select>
                                        </div>

                                        <Input
                                            label={t('apiKeys.modal.fields.value')}
                                            placeholder={t('apiKeys.modal.fields.valuePlaceholder')}
                                            value={apiKeyForm.value}
                                            onChange={(value) => setApiKeyForm({ ...apiKeyForm, value: value })}
                                            isRequired
                                            size="md"
                                        />

                                        <Input
                                            label={t('apiKeys.modal.fields.description')}
                                            placeholder={t('apiKeys.modal.fields.descriptionPlaceholder')}
                                            value={apiKeyForm.description}
                                            onChange={(value) => setApiKeyForm({ ...apiKeyForm, description: value })}
                                            size="md"
                                        />
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                            <Input
                                                label={t('serviceConfigs.modal.fields.name')}
                                                placeholder={t('serviceConfigs.modal.fields.namePlaceholder')}
                                                value={serviceConfigForm.name}
                                                onChange={(value) => setServiceConfigForm({ ...serviceConfigForm, name: value })}
                                                isRequired
                                                size="md"
                                            />
                                            <Select
                                                label={t('serviceConfigs.modal.fields.category')}
                                                items={categoryOptions.filter(opt => opt.id !== '')}
                                                selectedKey={serviceConfigForm.category}
                                                onSelectionChange={(key) => setServiceConfigForm({ ...serviceConfigForm, category: key as ServiceCategory })}
                                                isRequired
                                                size="md"
                                            >
                                                {(item) => (
                                                    <Select.Item key={item.id} id={item.id}>
                                                        {item.label}
                                                    </Select.Item>
                                                )}
                                            </Select>
                                        </div>

                                        <Input
                                            label={t('serviceConfigs.modal.fields.baseUrl')}
                                            placeholder={t('serviceConfigs.modal.fields.baseUrlPlaceholder')}
                                            value={serviceConfigForm.baseUrl}
                                            onChange={(value) => setServiceConfigForm({ ...serviceConfigForm, baseUrl: value })}
                                            size="md"
                                        />

                                        <Input
                                            label={t('serviceConfigs.modal.fields.config')}
                                            placeholder={t('serviceConfigs.modal.fields.configPlaceholder')}
                                            value={serviceConfigForm.config}
                                            onChange={(value) => setServiceConfigForm({ ...serviceConfigForm, config: value })}
                                            size="md"
                                        />
                                    </div>
                                )}

                                <div className="flex items-center justify-end gap-4 mt-6">
                                    <Button
                                        onClick={handleCloseModal}
                                        size="md"
                                        color="secondary"
                                    >
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button
                                        onClick={modalType === 'api-key' ? handleSaveApiKey : handleSaveServiceConfig}
                                        size="md"
                                        color="primary"
                                    >
                                        {editingItem ? t('modal.save') : t('modal.create')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </div>
    );
};
